<template>
  <div class="flex flex-col gap-2">
    <div class="flex items-center justify-between">
      <span class="text-sm font-medium text-gray-700 flex items-center gap-1">
        <FileText class="w-4 h-4" />
        Resume for this application
      </span>
    </div>
    <span class="text-xs text-gray-500">The selected resume will be used for the smart match assessment. It will also be included in the referral request, if you're approved for a referral.</span>

    <div v-if="loadingResumes" class="text-xs text-gray-400">Loading resumes…</div>

    <template v-else-if="canEdit">
      <template v-if="approvedResumes.length > 0">
        <div class="flex items-center gap-2">
          <Select
            :model-value="selectedResumeIdStr"
            @update:model-value="onResumeChange"
          >
            <SelectTrigger class="flex-1 text-sm bg-white">
              <SelectValue :placeholder="currentResume ? resumeLabel(currentResume) : 'No resume selected'" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">— No resume —</SelectItem>
              <SelectItem
                v-for="rf in approvedResumes"
                :key="rf.id"
                :value="String(rf.id)"
              >
                {{ resumeLabel(rf) }}
              </SelectItem>
            </SelectContent>
          </Select>

          <a
            v-if="currentResume?.display_url"
            :href="currentResume.display_url"
            target="_blank"
            class="shrink-0 p-1.5 rounded-md border border-gray-200 text-gray-500 hover:bg-gray-50"
            title="Open resume"
          >
            <ExternalLink class="w-4 h-4" />
          </a>
        </div>
      </template>

      <div v-else class="flex items-center gap-2">
        <p class="text-xs text-gray-400 italic flex-1">
          No approved resumes available. Upload and get a resume approved first.
        </p>
        <a
          v-if="currentResume?.display_url"
          :href="currentResume.display_url"
          target="_blank"
          class="shrink-0 p-1.5 rounded-md border border-gray-200 text-gray-500 hover:bg-gray-50"
          title="Open resume"
        >
          <ExternalLink class="w-4 h-4" />
        </a>
      </div>
    </template>

    <!-- Read-only (non-owner, non-guide) -->
    <template v-else>
      <div v-if="currentResume" class="flex items-center gap-2 text-sm">
        <span class="text-gray-700">{{ resumeLabel(currentResume) }}</span>
        <a
          v-if="currentResume.display_url"
          :href="currentResume.display_url"
          target="_blank"
          class="shrink-0 p-1.5 rounded-md border border-gray-200 text-gray-500 hover:bg-gray-50"
          title="Open resume"
        >
          <ExternalLink class="w-4 h-4" />
        </a>
      </div>
      <p v-else class="text-sm text-gray-400 italic">No resume selected for this application.</p>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { FileText, ExternalLink } from 'lucide-vue-next'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useToast } from '@/components/ui/toast'
import { useResumeFilesStore } from '@/stores/resumeFiles'
import type { ResumeFile } from '@/stores/resumeFiles'
import { useApplicationsStore } from '@/stores/applications'
import { useAuth } from '@/composables/useAuth'
import { useUserStore } from '@/stores/user'
import type { Application } from '@/types/application'
import type { AcceptableValue } from 'reka-ui'

const props = defineProps<{
  application: Application
  applicationId: string
}>()

const resumeFilesStore = useResumeFilesStore()
const applicationsStore = useApplicationsStore()
const userStore = useUserStore()
const { isGuide } = useAuth()
const { toast } = useToast()

const loadingResumes = ref(false)
const localResumes = ref<ResumeFile[]>([])

const isOwner = computed(() =>
  props.application.user_details?.id === userStore.currentUser?.id
)
const canEdit = computed(() => isOwner.value || isGuide.value)

const currentResume = computed(() => props.application.resume_file_details ?? null)

const selectedResumeId = ref<number | null>(props.application.resume_file ?? null)

const selectedResumeIdStr = computed(() =>
  selectedResumeId.value != null ? String(selectedResumeId.value) : ''
)

const approvedResumes = computed(() =>
  localResumes.value.filter((r) => r.approval_status === 'approved')
)

function resumeLabel(rf: { name?: string; file?: string; external_url?: string }): string {
  if (rf.name) return rf.name
  if (rf.file) return rf.file.split('/').pop() || rf.file
  return 'External resume'
}

async function loadResumes() {
  loadingResumes.value = true
  try {
    const userId = props.application.user_details?.id ?? props.application.user
    await resumeFilesStore.fetchResumeFiles(userId)
    localResumes.value = [...resumeFilesStore.resumeFiles]
  } finally {
    loadingResumes.value = false
  }
}

async function onResumeChange(value: AcceptableValue) {
  const str = value == null ? '' : String(value)
  const newId = (str === '' || str === '__none__') ? null : Number(str)
  const previousId = selectedResumeId.value
  const previousDetails = props.application.resume_file_details ?? null

  // Build optimistic resume_file_details from local store data
  const selected = newId != null ? approvedResumes.value.find((r) => r.id === newId) : null
  const newDetails = selected
    ? {
        id: selected.id,
        name: resumeLabel(selected),
        display_url: selected.file || selected.external_url,
        approval_status: selected.approval_status as 'approved',
      }
    : null

  // Apply optimistically — mutate in place to avoid replacing the object reference
  // (replacing the reference causes the entire parent to re-render)
  selectedResumeId.value = newId
  if (applicationsStore.selectedApplication) {
    Object.assign(applicationsStore.selectedApplication, {
      resume_file: newId,
      resume_file_details: newDetails,
    })
  }

  try {
    // background = true skips isLoadingSelected, preventing the parent re-render
    await applicationsStore.updateApplication(props.applicationId, {
      resume_file: newId,
    } as Partial<Application>, true)
    toast({
      title: newId ? 'Resume updated' : 'Resume cleared',
      description: newId ? `Now using "${resumeLabel(selected!)}"` : 'No resume selected for this application.',
    })
  } catch (err: any) {
    // Revert on failure — also in place
    selectedResumeId.value = previousId
    if (applicationsStore.selectedApplication) {
      Object.assign(applicationsStore.selectedApplication, {
        resume_file: previousId,
        resume_file_details: previousDetails,
      })
    }
    const message = err?.response?.data?.resume_file?.[0] ?? 'Failed to update resume.'
    toast({ title: 'Error', description: message, variant: 'destructive' })
  }
}

watch(
  () => props.application.resume_file,
  (val) => { selectedResumeId.value = val ?? null },
)

onMounted(async () => {
  if (canEdit.value) {
    await loadResumes()
  }
})
</script>
